class SwiperSlider extends HTMLElement {
  constructor() {
    super();
    this.swiper = null;
    this.config = null;
    this.thumbsSwiper = null;

    // Get breakpoint values
    this.breakpointMax = parseInt(this.getAttribute('data-breakpoint-max')) || null;
    this.breakpointMin = parseInt(this.getAttribute('data-breakpoint-min')) || null;
  }

  debounce(fn, delay = 100) {
    let timeout;
    return (...args) => {
      clearTimeout(timeout);
      timeout = setTimeout(() => fn.apply(this, args), delay);
    };
  }

  connectedCallback() {
    this.loadConfig();
    this.scheduleInitSwiper();
    this.initDetailsListeners();

    this.debouncedResizeHandler = this.debounce(this.handleResize.bind(this), 100);

    window.addEventListener('resize', this.debouncedResizeHandler);
    this._onWindowLoad = () => {
      if (this.swiper) {
        this.swiper.update();
      } else if (this.shouldInitializeSwiper()) {
        this.initSwiper();
      }
    };
    window.addEventListener('load', this._onWindowLoad);
  }

  initDetailsListeners() {
    // Find all details elements inside the swiper
    const detailsElement = this.closest('details');

    if (detailsElement) {
      // For each details element, add a toggle event listener
      detailsElement.addEventListener('toggle', (event) => {
        // If the details element is open, reload the swiper
        if (detailsElement.open) {
          // Use setTimeout to ensure the details content is fully rendered
          setTimeout(() => {
            this.reload();
          }, 100);
        }
      });
    }
  }

  disconnectedCallback() {
    this.destroySwiper();
    window.removeEventListener('resize', this.debouncedResizeHandler);
    if (this._onWindowLoad) {
      window.removeEventListener('load', this._onWindowLoad);
    }

  }

  scheduleInitSwiper(forceInit) {
    requestAnimationFrame(() => {
      requestAnimationFrame(() => {
        this.initSwiper(forceInit);
      });
    });
  }

  loadConfig() {
    const script = this.querySelector('script[type="application/json"]');
    if (script) {
      try {
        this.config = JSON.parse(script.textContent);
      } catch (e) {
        console.error('Invalid JSON configuration in <swiper-slider>: ', e);
      }
    } else {
      this.config = {};
    }
  }

  reload() {
    this.destroySwiper();

    // Use setTimeout to ensure the modal is fully rendered before initializing Swiper
    setTimeout(() => {
      this.scheduleInitSwiper(true);
    }, 50);
  }

  shouldInitializeSwiper() {
    const windowWidth = window.innerWidth;

    if (this.breakpointMax && windowWidth >= this.breakpointMax) return false;
    if (this.breakpointMin && windowWidth < this.breakpointMin) return false;
    return true;
  }

  initSwiper(forceInit) {
    // Don't initialize if outside of breakpoint range
    if (!forceInit && !this.shouldInitializeSwiper()) {
      return;
    }

    const defaultConfig = {
      slidesPerView: 1,
      spaceBetween: 0,
      loop: false,
      draggable: true,
      allowTouchMove: true,
      grabCursor: true,
      mousewheel: {
        forceToAxis: true,
        releaseOnEdges: true,
      },
      keyboard: {
        enabled: true,
      },
    };

    const swiperConfig = {
      ...defaultConfig,
      ...this.config,
    };

    // Initialize thumbs swiper if thumbs config exists
    if (this.config.thumbs) {
      const thumbsElement = document.querySelector(this.config.thumbs.el);

      if (thumbsElement) {
        const thumbsConfig = {
          watchSlidesProgress: true,
          slideToClickedSlide: true,
          ...(this.config.thumbs.config || {}),
        };

        this.thumbsSwiper = new Swiper(thumbsElement, thumbsConfig);

        swiperConfig.thumbs = {
          swiper: this.thumbsSwiper,
        };
      }
    }

    const slidesWrapper = this.querySelector('.swiper-wrapper');

    if (!slidesWrapper) {
      console.error('Cannot find .swiper-wrapper element');
      return;
    }

    const containerWidth = this.clientWidth;
    const slidesWidth = slidesWrapper.scrollWidth;
    const wantsScrollbar = !!(this.config && this.config.scrollbar);

    // Ne pas sauter l'init si un scrollbar est configuré : la mesure peut être fausse au premier frame
    // (polices, sous-pixels), ce qui casse le scroll tactile sur Android / Windows.
    if (!forceInit && !wantsScrollbar && slidesWidth <= containerWidth) {
      console.log('All slides are visible, skipping Swiper initialization');
      return;
    }

    // Initialize main Swiper
    try {
      this.swiper = new Swiper(this, swiperConfig);
    } catch (e) {
      console.error('Swiper initialization failed: ', e);
    }

    if (this.swiper && wantsScrollbar) {
      this.swiper.update();
      if (typeof ResizeObserver !== 'undefined' && !this._scrollbarResizeObserver) {
        this._scrollbarResizeObserver = new ResizeObserver(() => {
          if (this.swiper) this.swiper.update();
        });
        this._scrollbarResizeObserver.observe(this);
      }
    }

    const fractionEl = this.querySelector('.swiper-pagination-fraction');
    if (fractionEl && this.swiper) {
      const updateFraction = () => {
        // Si en mode loop, le nombre total est recalculé en excluant les slides dupliquées
        const current = this.swiper.realIndex + 1;
        fractionEl.textContent = current + ' / ' + this.swiper.slides.length;
      };

      // MàJ dès l'initialisation et à chaque changement de slide
      this.swiper.on('init', updateFraction);
      this.swiper.on('slideChange', updateFraction);
      // Pour forcer la première mise à jour
      updateFraction();
    }
  }

  destroySwiper() {
    if (this._scrollbarResizeObserver) {
      this._scrollbarResizeObserver.disconnect();
      this._scrollbarResizeObserver = null;
    }
    if (this.thumbsSwiper) {
      this.thumbsSwiper.destroy(true, true);
      this.thumbsSwiper = null;
    }
    if (this.swiper) {
      this.swiper.destroy(true, true);
      this.swiper = null;
    }
  }

  handleResize() {
    const prevConfig = this.config;
    this.loadConfig();

    // Check if we should initialize or destroy based on breakpoints
    const shouldInit = this.shouldInitializeSwiper();

    if (shouldInit && !this.swiper) {
      // Initialize if we're within breakpoints and swiper doesn't exist
      this.scheduleInitSwiper();
    } else if (!shouldInit && this.swiper) {
      // Destroy if we're outside breakpoints and swiper exists
      this.destroySwiper();
    } else if (shouldInit && this.swiper && JSON.stringify(prevConfig) !== JSON.stringify(this.config)) {
      // Reinitialize if config has changed and we're within breakpoints
      this.destroySwiper();
      this.scheduleInitSwiper();
    }
  }
}

customElements.define('swiper-slider', SwiperSlider);
